<template>
    <div class="container-fluid">
        <h1>{{ $t('systems') }}</h1>

        <div class="d-flex justify-content-between mb-3 flex-wrap-reverse align-items-center">
            <b-pagination
                :total-rows="systemsCount"
                :per-page="perPage"
                v-model="currentPage"
                size="sm"
                variant="secondary"
                class="mb-0"
                hide-goto-end-buttons
            />

            <span>{{ $t('x_to_y_of_z', pageRange) }}</span>

            <b-button @click.stop="newSystem()" size="sm" ref="buttonNewSystem">
                <font-awesome-icon icon="plus"></font-awesome-icon>
                {{ $t('new_system') }}
            </b-button>
        </div>

        <!-- <pre style="color: black;">{{ systems }}</pre> -->

        <b-table dark :items="systems" small :fields="tableFields" :class="$style.thetable" responsive>
            <template slot="HEAD_name" slot-scope="data">
                <font-awesome-icon icon="sign-in-alt" class="th-icon"></font-awesome-icon>
                <span class="th-text">{{ $t('name') }}</span>
            </template>
            <template slot="HEAD_type" slot-scope="data">
                <font-awesome-icon icon="sign-in-alt" class="th-icon"></font-awesome-icon>
                <span class="th-text">{{ $t('type') }}</span>
            </template>
            <template slot="HEAD_wealth" slot-scope="data">
                <font-awesome-icon icon="sign-in-alt" class="th-icon"></font-awesome-icon>
                <span class="th-text">{{ $t('wealth') }}</span>
            </template>
            <template slot="HEAD_sell" slot-scope="data">
                <font-awesome-icon icon="sign-out-alt" class="th-icon"></font-awesome-icon>
                <span class="th-text">{{ $t('sell')}}</span>
            </template>
            <template slot="HEAD_buy" slot-scope="data">
                <font-awesome-icon icon="sign-in-alt" class="th-icon"></font-awesome-icon>
                <span class="th-text">{{ $t('buy') }}</span>
            </template>
            <template slot="bar" slot-scope="data">
                <eco-bar :color="getEcoColor(data.item.ecoSubType)" :tier="getEcoTier(data.item.wealth)"></eco-bar>
            </template>
            <template slot="type" slot-scope="data">{{ $t(data.item.ecoSubType) }}</template>
            <template slot="wealth" slot-scope="data">
                {{ $t(data.item.wealth) }}
            </template>
            <template slot="sell" slot-scope="data">{{ data.item.sell ? `${data.item.sell.toFixed(1)}%` : '-'}}</template>
            <template slot="buy" slot-scope="data">{{ data.item.buy ? `${data.item.buy.toFixed(1)}%` : '-'}}</template>
            <template slot="actions" slot-scope="data">
                <div class="d-flex">
                    <font-awesome-icon icon="edit" @click.stop="editSystem(data.item)" ></font-awesome-icon>
                    <font-awesome-icon icon="trash" @click.stop="removeSystem(data.item.id)" class="icon--action text-warning"></font-awesome-icon>
                </div>
            </template>
        </b-table>

        <collection-info @update="getSystems()"/>

        <div>Version {{ version }}</div>

        <b-modal
            id="systemDetails"
            ref="systemDetails"
            :class="$style.modal"
            :title="tempSystem.id ? $t('edit_system') : $t('new_system')"
            size="lg"
            @ok="tempSystem.id ? saveEdit() : addSystem()"
            @hidden="$refs.buttonNewSystem.focus()"
            @shown="$refs.systemname.focus()"
            :ok-disabled="!formValid"
            :ok-title="tempSystem.id ? $t('save') : $t('add')"
            :cancel-title="$t('cancel')"
            >
            <b-form id="Form">
                <div :class="$style['form-grid']">
                    <b-form-group :label="$t('system_name')" :class="$style.systemName">
                        <b-form-input v-model="tempSystem.name" size="sm" ref="systemname" :state="validName()"/>
                    </b-form-group>
                    <b-form-group :label="$t('economy_type')" :class="$style.economyType">
                        <b-form-select v-model="tempSystem.ecoSubType" size="sm" :state="validSelect(tempSystem.ecoSubType)">
                            <optgroup v-for="(group, index) in economies.types" :label="$t(group.title)" :key="index">
                                <option v-for="(sub, index) in group.subs" :value="sub" :key="index">{{ $t(sub) }}</option>
                            </optgroup>
                        </b-form-select>
                    </b-form-group>
                    <b-form-group :label="$t('system_wealth')" :class="$style.systemWealth">
                        <b-form-select v-model="tempSystem.wealth" size="sm" :state="validSelect(tempSystem.wealth)">
                            <optgroup v-for="(group, index) in economies.levels" :label="$t(group.title)" :key="index">
                                <option v-for="(sub, index) in group.subs" :value="sub" :key="index">{{ $t(sub) }}</option>
                            </optgroup>
                        </b-form-select>
                    </b-form-group>
                    <b-form-group :label="$t('sell') + ' %'" :class="$style.sell">
                        <b-form-input v-model.number="tempSystem.sell" size="sm" :state="validSell()"/>
                        <b-form-text id="inputLiveHelp">
                            {{ $t('enter_value_from_to', ['40.0','80.0'])}}
                        </b-form-text>
                    </b-form-group>
                    <b-form-group :label="$t('buy') + ' (-) %'" :class="$style.buy">
                        <b-form-input v-model.number="tempSystem.buy" size="sm" :state="validBuy()"/>
                        <b-form-text id="inputLiveHelp">
                            {{ $t('enter_value_from_to', ['-30.0','-10.0'])}}
                        </b-form-text>
                    </b-form-group>
                    <b-check v-model="tempSystem.hidden" :class="$style.check">
                        {{ $t('hide_from_trading_loop') }}
                        <font-awesome-icon icon="eye-slash" @click.stop="editSystem(data.item)" ></font-awesome-icon>
                    </b-check>
                </div>
            </b-form>
            <!-- <p>{{ formValid ? 'valid' : 'invalid'}}</p> -->
        </b-modal>
    </div>
</template>

<script>
import Vue from 'vue';
import bButton from 'bootstrap-vue/es/components/button/button';
import bCheck from 'bootstrap-vue/es/components/form-checkbox/form-checkbox';
import bForm from 'bootstrap-vue/es/components/form/form';
import bFormGroup from 'bootstrap-vue/es/components/form-group/form-group';
import bFormInput from 'bootstrap-vue/es/components/form-input/form-input';
import bFormSelect from 'bootstrap-vue/es/components/form-select/form-select';
import bFormText from 'bootstrap-vue/es/components/form/form-text';
import bModal from 'bootstrap-vue/es/components/modal/modal';
import bPagination from 'bootstrap-vue/es/components/pagination/pagination';
import bTable from 'bootstrap-vue/es/components/table/table';
import EcoBar from '@/components/EcoBar';
import EcoIcon from '@/components/EcoIcon';
import CollectionInfo from '@/components/CollectionInfo';
import vBModal from 'bootstrap-vue/es/directives/modal/modal';
import SystemService from '@/services/systemService';
import utils from '@/mixins/utils';
const pjson = require('../../package.json').version;

Vue.directive('b-modal', vBModal);

export default {
    data () {
        return {
            systems: [],
            tempSystem: {},
            currentPage: 1,
            perPage: 20,
            systemsCount: 0,
            version: pjson,
            tableFields: [
                { key: 'name', label: this.$t('name') },
                { key: 'bar', label: '' },
                { key: 'type', label: this.$t('type') },
                { key: 'wealth', label: this.$t('wealth') },
                { key: 'sell', label: this.$t('sell') },
                { key: 'buy', label: this.$t('buy') },
                // { key: 'userHash', label: '#' },
                { key: 'actions', label: '' }
            ]
        };
    },
    mixins: [utils],
    components: {
        bButton,
        bCheck,
        bForm,
        bFormGroup,
        bFormInput,
        bFormSelect,
        bFormText,
        bModal,
        bPagination,
        bTable,
        CollectionInfo,
        EcoBar,
        EcoIcon
    },
    computed: {
        totalPages () {
            return Math.ceil(this.systemsCount / this.perPage);
        },
        pageRange () {
            const from = this.perPage * (this.currentPage - 1) + 1;
            const rest = this.systemsCount - (this.perPage * (this.currentPage - 1));
            const to = rest < this.perPage ? from + rest - 1 : from + this.perPage - 1;
            const total = this.systemsCount;

            return [from, to, total];
        },
        economies () {
            return {
                types: this.$root.shared.economyTypes,
                levels: this.$root.shared.economyLevels
            };
        },
        typeOptions () {
            return this.$root.shared.economyTypes.map(type => {
                return { value: type.title, text: this.$t(type.title) };
            });
        },
        formatedSystems () {
            return this.getSystems.map(s => {
                s.type = this.$t(s.type);
                return s;
            });
        },
        formValid () {
            return this.tempSystem.name !== '' && this.validSell() && this.validBuy() && this.tempSystem.ecoSubType && this.tempSystem.wealth;
        },
    },
    methods: {
        async addSystem () {
            this.tempSystem.tier = this.getEcoTier(this.tempSystem.wealth);
            this.tempSystem.ecoType = this.getEcoCategory(this.tempSystem.ecoSubType);
            await SystemService.newSystem(this.tempSystem);
            await this.getSystems();
            await this.clearInputs();
            // this.$refs.buttonNewSystem.focus();
        },
        async getSystems () {
            const data = (await SystemService.getSystems({
                userHash: this.$ls.get('userHash'),
                page: this.currentPage,
                perPage: this.perPage,
            })).data;

            this.systems = data.systems;
            this.systemsCount = data.total;
        },
        async removeSystem (id) {
            await SystemService.deleteSystem({ id: id });
            await this.getSystems();
        },
        async newSystem () {
            this.clearInputs();
            await this.$refs.systemDetails.show();
            await this.$refs.systemname.focus();
        },
        editSystem (system) {
            this.tempSystem = Object.assign({}, system);
            this.$refs.systemDetails.show();
        },
        cancelEdit () {
            this.clearInputs();
        },
        async saveEdit () {
            this.tempSystem.tier = this.getEcoTier(this.tempSystem.wealth);
            this.tempSystem.ecoType = this.getEcoCategory(this.tempSystem.ecoSubType);
            await SystemService.updateSystem(this.tempSystem);
            await this.cancelEdit();
            await this.getSystems();
        },
        clearInputs () {
            this.tempSystem = Object.assign({}, {
                name: '',
                sell: null,
                buy: null,
                ecoSubType: null,
                wealth: null,
                tier: null,
                hidden: false,
                userHash: this.$ls.get('userHash')
            });
            this.$refs.systemname.focus();
        },
        validSell () {
            return this.validPercent(this.tempSystem.sell, 40, 80);
        },
        validBuy () {
            return this.validPercent(this.tempSystem.buy, -30, -10);
        },
        validPercent (val, min, max) {
            if (val === '' || val === null) return true;
            switch (typeof val) {
                case ('string'):
                    return false;
                case ('number'):
                    if (val < min || val > max) return false;
                    return true;
                default:
                    return null;
            }
        },
        validSelect(val) {
            return val !== null;
        },
        validName() {
            return this.tempSystem.name !== '';
        },
        focusName() {
            this.$refs.systemname.focus();
        }
    },
    async mounted () {
        this.getSystems();
    },
    watch: {
        currentPage () {
            this.getSystems();
        }
    }
};
</script>

<style module lang="scss" scoped>
    .thetable {
        // margin-bottom: 2em;
    }

    .thetable #{"/deep/"} td {
        vertical-align: middle;
    }

    .thetable #{"/deep/"} td:last-child {
        border-left: 1px solid white;
        padding-left: 0.5em;
        padding-right: 0.5em;
    }

    .thetable #{"/deep/"} th,
    .thetable #{"/deep/"} td {
        white-space: nowrap;
        // padding: 0 0.5em;
    }

    .thetable #{"/deep/"} th:nth-child(3),
    .thetable #{"/deep/"} td:nth-child(3),
    .thetable #{"/deep/"} th:nth-child(4),
    .thetable #{"/deep/"} td:nth-child(4) {
        display: none;

        @media(min-width: 30em) {
            display: table-cell;
        }
    }

    .thetable #{"/deep/"} th:nth-child(5),
    .thetable #{"/deep/"} td:nth-child(5),
    .thetable #{"/deep/"} th:nth-child(6),
    .thetable #{"/deep/"} td:nth-child(6) {
        &,
        span {
            text-align: end;
        }
    }

    .thetable #{"/deep/"} th:nth-child(7),
    .thetable #{"/deep/"} td:nth-child(7) {
        width: 1px;
    }

    .form-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        grid-column-gap: 1em;
    }

    .form-grid *:first-child,
    .check {
        grid-column: 1 / -1;
    }

    .economyType,
    .systemWealth {
        grid-column: auto / span 4;

        @media(min-width: 30em) {
            grid-column: auto / span 2;
        }

        @media(min-width: 992px) {
            grid-column: auto / span 1;
        }
    }

    .sell,
    .buy {
        grid-column: auto / span 2;

        @media(min-width: 992px) {
            grid-column: auto / span 1;
        }
    }

    .thetable #{"/deep/"} [class*="fa-"] + [class*="fa-"] {
        margin-left: 0.5em;
    }

    .modal {
        color: black;
    }
</style>
